import { type FC, useEffect, useState } from 'react';
import { supabase } from '@/lib/api/supabase';
import { TournamentCard } from '@/features/tournaments/components/TournamentCard';
import { useNavigate } from 'react-router-dom';
import type { Tournament } from '@/types';
import { dbToTournament } from '@/types';

export interface TournamentGalleryProps {
  currentTournamentId: string;
  weapon?: string;
  division?: string;
  limit?: number;
}

/**
 * Tournament gallery component that displays related tournaments
 * at the bottom of tournament detail pages
 */
export const TournamentGallery: FC<TournamentGalleryProps> = ({
  currentTournamentId,
  weapon,
  division,
  limit = 6,
}) => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRelatedTournaments = async () => {
      try {
        setLoading(true);

        let query = supabase
          .from('tournaments')
          .select('*')
          .eq('visible', true)
          .neq('id', currentTournamentId)
          .in('status', ['open', 'draft'])
          .order('date', { ascending: true })
          .limit(limit);

        // Prioritize same weapon type if specified
        if (weapon) {
          query = query.eq('weapon', weapon);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching related tournaments:', error);
          setTournaments([]);
          return;
        }

        if (data) {
          const tournamentList = data.map((t) => dbToTournament(t));
          setTournaments(tournamentList);
        }
      } catch (err) {
        console.error('Failed to fetch tournaments:', err);
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchRelatedTournaments();
  }, [currentTournamentId, weapon, division, limit]);

  const handleRegister = (tournamentId: string) => {
    navigate('/event-registration', {
      state: { preselectedTournamentId: tournamentId },
    });
  };

  // Don't render anything if no tournaments or loading failed
  if (loading) {
    return (
      <section className="tournament-gallery mt-16 pt-8 border-t-2 border-white/20">
        <h2 className="text-3xl font-bold text-white mb-6">Other Tournaments</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-64 bg-white/10 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </section>
    );
  }

  if (tournaments.length === 0) return null;

  return (
    <section className="tournament-gallery mt-16 pt-8 border-t-2 border-white/20">
      <h2 className="text-3xl font-viking text-white mb-6">Other Tournaments</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament) => (
          <div
            key={tournament.id}
            className="tournament-card-wrapper transition-transform duration-300 hover:-translate-y-1"
          >
            <TournamentCard
              tournament={tournament}
              onRegister={handleRegister}
            />
          </div>
        ))}
      </div>
    </section>
  );
};
